import React, { useId, useMemo } from 'react';
import { View } from 'react-native';
import Svg, { Circle, ClipPath, Defs, G, LinearGradient, Path, Stop, } from 'react-native-svg';
import { rem } from 'design-system/values';
import { RingGraphBuilder } from 'design-system/RingGraph';
import useColors from 'hooks/useColors';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
export const DIMENSIONS = {
    L: {
        outerDiameter: rem(240),
        strokeWidth: rem(34),
        lineEndBorderRadius: rem(10),
        empty: {
            outerDiameter: rem(210),
            strokeWidth: rem(10),
            strokeDasharray: [rem(16), rem(12)],
        },
    },
    S: {
        outerDiameter: rem(120),
        strokeWidth: rem(18),
        lineEndBorderRadius: rem(6),
        empty: {
            outerDiameter: rem(102),
            strokeWidth: rem(6),
            strokeDasharray: [rem(10), rem(6)],
        },
    },
};
// The end of the spent colour gets the corner radius the arc's start would have
// for the spent length, but no less than this share of the stroke width. At
// most 0.5.
const SPENT_END_MIN_RADIUS_RATIO = 0.1;
// Spans the whole chart rather than the shape it fills, so both colours keep
// the same position on the ring whatever the arc lengths are.
const HorizontalGradient = ({ id, colors, radius }) => (<LinearGradient id={id} gradientUnits="userSpaceOnUse" x1={-radius} y1={0} x2={radius} y2={0}>
    <Stop stopColor={colors[0]}/>
    <Stop offset="1" stopColor={colors[1]}/>
  </LinearGradient>);
const toPositiveRatio = (ratio) => ratio != null && ratio > 0 ? ratio : 0;
// Full ring between the two radii, centred on the origin.
const buildRingPath = (outerRadius, innerRadius) => [outerRadius, innerRadius]
    .map((r) => `M 0 ${-r} A ${r} ${r} 0 1 1 0 ${r} A ${r} ${r} 0 1 1 0 ${-r} Z`)
    .join(' ');
// Pie slice from 12 o'clock clockwise through `span`, centred on the origin.
const buildWedgePath = (radius, span) => {
    if (span >= 2 * Math.PI) {
        return `M ${-radius} ${-radius} H ${radius} V ${radius} H ${-radius} Z`;
    }
    const x = radius * Math.sin(span);
    const y = -radius * Math.cos(span);
    return `M 0 0 L 0 ${-radius} A ${radius} ${radius} 0 ${Number(span > Math.PI)} 1 ${x} ${y} Z`;
};
/**
 * For ratio props
 * - ratio is not a number (null or NaN) or ratio <= 0: renders no arc
 * - 0 < ratio <= 1: renders an arc
 * - ratio >= 1: renders a complete circle
 * */
const BudgetingCircleChart = ({ committedRatio, spentRatio, large, gradientColors, backgroundColor, isEmpty, }) => {
    const colors = useColors();
    const id = useId();
    const clipId = `clip_${id}`;
    const committedGradientId = `committedGradient_${id}`;
    const spentGradientId = `spentGradient_${id}`;
    const { empty, outerDiameter, strokeWidth, lineEndBorderRadius } = large
        ? DIMENSIONS.L
        : DIMENSIONS.S;
    const styles = useStyles(styleSet);
    const ringGraphBuilder = useMemo(() => new RingGraphBuilder({
        innerRadius: outerDiameter / 2 - strokeWidth,
        outerRadius: outerDiameter / 2,
        maxBorderRadius: lineEndBorderRadius,
    }), [lineEndBorderRadius, strokeWidth, outerDiameter]);
    const backgroundCircleProps = useMemo(() => ringGraphBuilder.buildCircleSvgProps(), [ringGraphBuilder]);
    // Both colours are drawn inside one arc shape, so its rounded start looks the
    // same whatever the two ratios are. The committed colour fills the whole arc
    // and the spent colour is a wedge over its start.
    const spentSpan = toPositiveRatio(spentRatio) * 2 * Math.PI;
    const arcSpan = Math.max(toPositiveRatio(committedRatio) * 2 * Math.PI, spentSpan);
    const outerRadius = outerDiameter / 2;
    const innerRadius = outerRadius - strokeWidth;
    // From a full circle on, the arc is a ring with no rounded start or end, so
    // the committed colour fills the gap between the spent colour's two ends.
    const arcPath = useMemo(() => {
        if (arcSpan <= 0)
            return null;
        return arcSpan >= 2 * Math.PI
            ? buildRingPath(outerRadius, innerRadius)
            : ringGraphBuilder.buildSectorPath(arcSpan);
    }, [arcSpan, outerRadius, innerRadius, ringGraphBuilder]);
    // Where the spent colour ends and the committed one continues, the spent part
    // gets rounded corners. They stop at the spent angle, so the spent wedge ends
    // `cornerSpan` before it and a rounded sector covers the rest.
    const spentPaths = useMemo(() => {
        if (spentSpan <= 0)
            return null;
        // Once the spent colour has lapped, its end lies over its own start, so the
        // whole ring is the spent colour.
        if (spentSpan >= arcSpan || spentSpan > 2 * Math.PI) {
            return { wedge: buildWedgePath(outerDiameter, spentSpan), end: null };
        }
        // The corners the start would have if the arc were only the spent part, so
        // they shrink when the spent part is short.
        const cornerRadius = Math.max(ringGraphBuilder.getBorderRadius(spentSpan), strokeWidth * SPENT_END_MIN_RADIUS_RATIO);
        // The corners take more angle on the inner edge than on the outer one.
        const cornerSpan = Math.asin(cornerRadius / (innerRadius + cornerRadius));
        const endBuilder = new RingGraphBuilder({
            innerRadius,
            outerRadius,
            maxBorderRadius: cornerRadius,
        });
        // When only the committed colour fills the circle, it runs on under the
        // spent colour's start too, so the spent part gets the same corners at both
        // ends.
        if (arcSpan >= 2 * Math.PI) {
            return { wedge: null, end: endBuilder.buildSectorPath(spentSpan) };
        }
        return {
            wedge: spentSpan > cornerSpan
                ? buildWedgePath(outerDiameter, spentSpan - cornerSpan)
                : null,
            end: endBuilder.buildSectorPath(2 * cornerSpan, spentSpan - 2 * cornerSpan),
        };
    }, [
        spentSpan,
        arcSpan,
        outerDiameter,
        strokeWidth,
        innerRadius,
        outerRadius,
        ringGraphBuilder,
    ]);
    const isEmptyWithCircleProps = useMemo(() => {
        if (!isEmpty)
            return null;
        const builder = new RingGraphBuilder({
            innerRadius: (outerDiameter - strokeWidth) / 2,
            outerRadius: outerDiameter / 2,
            maxBorderRadius: 0, // not useful for circles
        });
        return builder.buildCircleSvgProps();
    }, [outerDiameter, strokeWidth, isEmpty]);
    if (isEmptyWithCircleProps) {
        return (<View>
        <Svg width={outerDiameter} height={outerDiameter} viewBox={`0 0 ${outerDiameter} ${outerDiameter}`}>
          <Circle {...isEmptyWithCircleProps} strokeDasharray={empty.strokeDasharray} stroke={backgroundColor ?? colors.background.dark}/>
        </Svg>
      </View>);
    }
    return (<View>
      <Svg width={outerDiameter} height={outerDiameter} viewBox={`0 0 ${outerDiameter} ${outerDiameter}`}>
        <Circle {...backgroundCircleProps} stroke={backgroundColor ?? colors.background.dark}/>
      </Svg>
      {/* viewBox centred on the origin, as RingGraphBuilder's paths are */}
      <Svg width={outerDiameter} height={outerDiameter} viewBox={`${-outerRadius} ${-outerRadius} ${outerDiameter} ${outerDiameter}`} style={styles.shadow}>
        {arcPath != null && (<>
            <Defs>
              <ClipPath id={clipId}>
                <Path d={arcPath} clipRule="evenodd"/>
              </ClipPath>
              <HorizontalGradient id={committedGradientId} colors={colors.gradients.darkPink.colors} radius={outerRadius}/>
              <HorizontalGradient id={spentGradientId} colors={gradientColors ?? colors.gradients.ultimate.colors} radius={outerRadius}/>
            </Defs>
            <G clipPath={`url(#${clipId})`}>
              <Path d={buildWedgePath(outerDiameter, 2 * Math.PI)} fill={`url(#${committedGradientId})`}/>
              {spentPaths?.wedge != null && (<Path d={spentPaths.wedge} fill={`url(#${spentGradientId})`}/>)}
              {spentPaths?.end != null && (<Path d={spentPaths.end} fill={`url(#${spentGradientId})`}/>)}
            </G>
          </>)}
      </Svg>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    shadow: {
        position: 'absolute',
        ...colors.shadows.budgetingChartShadow,
    },
}));
export default BudgetingCircleChart;
